import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from "@mui/material";
import { isDisabledByRule } from "components/Omnisearch/disableFilterWhen";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { usePrevNextNavigationContext } from "components/PrevNextNavigation";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { TierIiReportKind } from "generated-graphql/graphql";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useCallback, useEffect } from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

import { useBreadcrumb } from "../../../hooks/useBreadcrumbs";
import { REPORT_KIND_DISABLED_WHEN } from "./AssignmentManagerTable/chemicalFacilitiesFilters";
import { DropRuledOutReportKindOnOpen } from "./DropRuledOutReportKindOnOpen";
import { FULFILLMENT_DEFAULT_REPORT_KINDS } from "./fulfillmentDefaults";
import { StatsProvider, useStats } from "./StatsContext";
import { WorkflowTagsButton } from "./WorkflowTagsFilter";

const isTierIiReportKind = (value: string): value is TierIiReportKind =>
  Object.values<string>(TierIiReportKind).includes(value);

function ReportTypePicker({
  reportKinds,
  setReportKind,
  disabledReason,
}: {
  reportKinds: TierIiReportKind[];
  setReportKind: (reportKind: TierIiReportKind[] | undefined) => void;
  /** Disables the picker and shows this as its tooltip. */
  disabledReason?: string;
}) {
  const theme = useTheme();
  return (
    <Tooltip title={disabledReason ?? ""}>
      {/* A disabled control fires no hover events, so the tooltip listens on this wrapper. */}
      <span>
        <FormControl sx={{ width: theme.spacing(20) }} disabled={disabledReason !== undefined}>
          <InputLabel id="report-type" shrink>
            Report Type(s)
          </InputLabel>
          <Select
            variant="standard"
            sx={{
              "&.MuiInputBase-root": {
                marginTop: "0.375rem",
              },
            }}
            fullWidth
            multiple
            labelId="report-type"
            label="Report Type(s)"
            value={reportKinds}
            displayEmpty
            MenuProps={{
              // Wider than the field so long labels (e.g. "Newly Not Reporting") keep side padding
              disableScrollLock: true,
              PaperProps: {
                sx: { minWidth: theme.spacing(30) },
              },
            }}
            onChange={(event) => {
              // MUI hands back a comma-joined string when the browser autofills the field.
              const { value } = event.target;
              const selected: string[] = typeof value === "string" ? value.split(",") : value;
              setReportKind(selected.filter(isTierIiReportKind));
            }}
            renderValue={(value) => {
              if (!value || value.length === 0) {
                return <span>All</span>;
              }
              return value.map((v) => prettyPrintEnumValue(v)).join(", ");
            }}
          >
            {Object.values(TierIiReportKind).map((reportKind) => (
              <MenuItem
                key={reportKind}
                value={reportKind}
                sx={{ paddingLeft: 1, paddingRight: 2, paddingY: 0 }}
              >
                <Checkbox checked={reportKinds.includes(reportKind)} />
                {prettyPrintEnumValue(reportKind)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </span>
    </Tooltip>
  );
}

/** Why the header's Report Type(s) can't be used with the current search, if it can't. */
function reportKindDisabledReason(view: string, omnisearch: string): string | undefined {
  // The rule comes from the Chemical Facilities grid's query.
  if (view !== "facilities") return undefined;
  return isDisabledByRule(REPORT_KIND_DISABLED_WHEN, parseOmnisearch(omnisearch))
    ? REPORT_KIND_DISABLED_WHEN.reason
    : undefined;
}

function FulfillmentDashboardContent() {
  const theme = useTheme();
  const backgroundColor = theme.palette.canvas;
  const navigate = useNavigate();
  const location = useLocation();
  const facilitiesMatch = useMatch("/staff/fulfillment/facilities");
  const workflowsMatch = useMatch("/staff/fulfillment/workflows");
  const waitForAllMatch = useMatch("/staff/fulfillment/wait-for-all");
  const errorClusteringMatch = useMatch("/staff/fulfillment/error-clustering");
  const wasteFulfillmentMatch = useMatch("/staff/fulfillment/waste");
  const manifestReviewMatch = useMatch("/staff/fulfillment/waste/manifest-review");
  const overviewMatch =
    facilitiesMatch ||
    workflowsMatch ||
    waitForAllMatch ||
    errorClusteringMatch ||
    wasteFulfillmentMatch ||
    manifestReviewMatch;

  const { statsOpen, handleToggle } = useStats();

  const { omnisearch, getOmnisearchValue, setOmnisearch } = useOmnisearchInput({
    isURLDriven: true,
    initialFilters: [],
  });

  // Helper function to properly parse workflow tags from omnisearch
  const getWorkflowTagsFromOmnisearch = useCallback((): string[] => {
    if (!omnisearch) {
      return [];
    }

    // Use extractKeyValuePairs to properly handle quoted values with pipes
    const pairs = extractKeyValuePairs(omnisearch);
    const workflowTagsPair = pairs.find((p) => p.key === "tags");

    let tags = workflowTagsPair?.value ?? [];

    // Check for special "null" value (untagged filter)
    if (tags.length === 1 && tags[0] === "null") {
      return [];
    }

    // If we got a single string with pipe separators, split it
    if (tags.length === 1 && typeof tags[0] === "string" && tags[0].includes("|")) {
      tags = tags[0]
        .split("|")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    }

    return tags;
  }, [omnisearch]);

  // Check if "untagged only" filter is active (tags:null)
  const isUntaggedOnly = useCallback((): boolean => {
    if (!omnisearch) return false;

    const pairs = extractKeyValuePairs(omnisearch);
    const workflowTagsPair = pairs.find((p) => p.key === "tags");

    return workflowTagsPair?.value?.length === 1 && workflowTagsPair.value[0] === "null";
  }, [omnisearch]);

  const { setNavigationKey } = usePrevNextNavigationContext();

  const getNewFilters = useCallback(
    (filters: Record<string, any>) => {
      const prev = parseOmnisearch(omnisearch);
      const reportKind = getOmnisearchValue("reportKind");
      const reportingYear = getOmnisearchValue("reportingYear");

      return {
        ...prev,
        reportKind,
        reportingYear,
        ...filters,
      };
    },
    [omnisearch, getOmnisearchValue]
  );

  // Determine current view based on the route
  const currentView = location.pathname.includes("/workflows")
    ? "workflow"
    : location.pathname.includes("/wait-for-all")
      ? "wait-for-all"
      : location.pathname.includes("/error-clustering")
        ? "error-clustering"
        : location.pathname.includes("/fulfillment/waste/manifest-review")
          ? "manifest-review"
          : location.pathname.includes("/fulfillment/waste")
            ? "waste"
            : "facilities";

  const handleYearChange = useCallback(
    (value: string | number | null) => {
      const newFilters = getNewFilters({ reportingYear: value?.toString() });

      setOmnisearch(stringifyOmnisearch(newFilters));
    },
    [getNewFilters, setOmnisearch]
  );

  const setReportKind = useCallback(
    (reportKinds: TierIiReportKind[] | undefined) => {
      let reportKindValue: string | undefined;

      if (!reportKinds || reportKinds.length === 0) {
        // User wants all types - explicitly set all to override default
        reportKindValue = Object.values(TierIiReportKind).join("|");
      } else if (
        reportKinds.length === 1 &&
        reportKinds[0] === FULFILLMENT_DEFAULT_REPORT_KINDS[0]
      ) {
        // Matches default - remove from omnisearch
        reportKindValue = undefined;
      } else {
        reportKindValue = reportKinds.join("|");
      }

      const newFilters = getNewFilters({ reportKind: reportKindValue });
      setOmnisearch(stringifyOmnisearch(newFilters));
    },
    [getNewFilters, setOmnisearch]
  );

  const setWorkflowTags = useCallback(
    (workflowTags: string[]) => {
      const joinedTags = workflowTags.length > 0 ? workflowTags.join("|") : undefined;

      const newFilters = getNewFilters({
        tags: joinedTags,
      });

      const newOmnisearch = stringifyOmnisearch(newFilters);

      setOmnisearch(newOmnisearch);
    },
    [getNewFilters, setOmnisearch]
  );

  const setUntaggedOnly = useCallback(
    (untagged: boolean) => {
      const newFilters = getNewFilters({
        tags: untagged ? "null" : undefined,
      });

      const newOmnisearch = stringifyOmnisearch(newFilters);
      setOmnisearch(newOmnisearch);
    },
    [getNewFilters, setOmnisearch]
  );

  useEffect(() => {
    // Set the navigation key when the component mounts
    setNavigationKey("/staff/fulfillment");

    // Reset the navigation key when the component unmounts
    return () => {
      setNavigationKey("");
    };
  }, [setNavigationKey]);

  useBreadcrumb({
    label: "Fulfillment",
    to: "/staff/fulfillment",
  });

  const handleViewChange = useCallback(
    (_event: React.SyntheticEvent, newView: string | null) => {
      // Only preserve reportingYear and reportKind when navigating between views
      // Clear all other grid-specific filters
      const reportingYear = getOmnisearchValue("reportingYear");
      const reportKind = getOmnisearchValue("reportKind");
      const workflowTags = getWorkflowTagsFromOmnisearch();
      const keepReportKind = newView === "facilities" || newView === "workflow";

      // Create the omnisearch query string with only shared filters
      const sharedFilters = {
        ...(reportingYear && { reportingYear }),
        ...(keepReportKind && reportKind && { reportKind }),
        // Only preserve workflow tags when navigating to workflow view
        ...(newView === "workflow" &&
          workflowTags.length > 0 && { workflowTags: workflowTags.join("|") }),
      };

      const cleanOmnisearchString = stringifyOmnisearch(sharedFilters);
      const omnisearchQuery =
        reportingYear ||
        (keepReportKind && reportKind) ||
        (newView === "workflow" && workflowTags.length > 0)
          ? `?omnisearch=${cleanOmnisearchString}`
          : "";

      // Update context state and defer navigation to avoid race condition
      setOmnisearch(cleanOmnisearchString);

      // Use setTimeout to ensure navigation happens after state update
      setTimeout(() => {
        if (newView === "facilities") {
          navigate(`/staff/fulfillment/facilities${omnisearchQuery}`);
        } else if (newView === "workflow") {
          navigate(`/staff/fulfillment/workflows${omnisearchQuery}`);
        } else if (newView === "wait-for-all") {
          navigate(`/staff/fulfillment/wait-for-all${omnisearchQuery}`);
        } else if (newView === "error-clustering") {
          navigate(`/staff/fulfillment/error-clustering`);
        } else if (newView === "waste") {
          navigate(`/staff/fulfillment/waste`);
        } else if (newView === "manifest-review") {
          navigate(`/staff/fulfillment/waste/manifest-review`);
        }
      }, 0);
    },
    [navigate, getOmnisearchValue, setOmnisearch, getWorkflowTagsFromOmnisearch]
  );

  const showReportTypePicker = currentView === "facilities" || currentView === "workflow";
  const showStatsToggle = currentView === "facilities" || currentView === "workflow";
  const showWorkflowTags = currentView === "workflow";

  return (
    <Stack sx={{ background: backgroundColor }}>
      {/* Toggle tabs at the top with white background */}
      {overviewMatch && (
        <Stack
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Tabs
            value={currentView}
            onChange={handleViewChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ px: 1, "& .MuiTab-root": { textTransform: "none", px: 3 } }}
          >
            <Tab value="facilities" label="Chemical Facilities" />
            <Tab value="workflow" label="Workflow Overview" />
            <Tab value="wait-for-all" label="Wait For All" />
            <Tab value="error-clustering" label="Error Triage" />
            <Tab value="waste" label="Waste Submissions" />
            <Tab value="manifest-review" label="Manifest Review" />
          </Tabs>
        </Stack>
      )}

      {/* Filters and Stats section with white background - only for chemical tabs. The waste
          tabs (Waste Submissions and Manifest Review) supply their own in-page filters, so the
          chemical-fulfillment header (reporting year, workflow tags, etc.) is suppressed there. */}
      {overviewMatch && !errorClusteringMatch && !wasteFulfillmentMatch && !manifestReviewMatch && (
        <Stack
          paddingX={3}
          paddingY={2}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={3} alignItems="center">
              <TierIIYearPicker
                showAllOption
                persistQueryParam={false}
                value={getOmnisearchValue("reportingYear")}
                onChange={handleYearChange}
              />
              {showReportTypePicker && (
                <ReportTypePicker
                  reportKinds={
                    getOmnisearchValue("reportKind")?.split("|").filter(isTierIiReportKind) ??
                    FULFILLMENT_DEFAULT_REPORT_KINDS
                  }
                  setReportKind={setReportKind}
                  disabledReason={reportKindDisabledReason(currentView, omnisearch)}
                />
              )}

              <WorkflowTagsButton
                selectedTags={showWorkflowTags ? getWorkflowTagsFromOmnisearch() : []}
                onTagsChange={setWorkflowTags}
                disabled={!showWorkflowTags}
                isUntaggedOnly={showWorkflowTags && isUntaggedOnly()}
                onUntaggedChange={setUntaggedOnly}
              />
            </Stack>

            {showStatsToggle && (
              <Button onClick={handleToggle} variant="text">
                {statsOpen ? "Hide Stats" : "Show Stats"}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      <Outlet />
    </Stack>
  );
}

export function FulfillmentDashboard() {
  return (
    <DropRuledOutReportKindOnOpen>
      <StatsProvider>
        <OmnisearchProvider>
          <FulfillmentDashboardContent />
        </OmnisearchProvider>
      </StatsProvider>
    </DropRuledOutReportKindOnOpen>
  );
}
